import { computed, nextTick, onScopeDispose, ref, unref, watch } from 'vue';
import ResizeObserver from 'resize-observer-polyfill';

import {
  Box,
  clamp,
  convertToUnit,
  destructComputed,
  flipAlign,
  flipCorner,
  flipSide,
  getAxis,
  getOverflow,
  getScrollParents,
  getTargetBox,
  IN_BROWSER,
  isFixedPosition,
  nullifyTransforms,
  parseAnchor,
  propsFactory
} from '@components/utils';
import { useDomObserver, useToggleScope } from '@components/composable';
import { NOTIFICATION_MARGIN, VIEWPORT_MARGIN } from '@components/components/CdsOverlay/constants';

import { anchorToPoint, getOffset } from '../utils';

import type { PropType, Ref, CSSProperties } from 'vue';
import type { TAnchor } from '@components/utils';

export interface ILocationStrategyData {
  contentEl: Ref<HTMLElement | undefined>;
  target: Ref<HTMLElement | [x: number, y: number] | undefined>;
  activatorEl: Ref<HTMLElement | undefined>;
  isActive: Ref<boolean>;
}

type TLocationStrategyFn = (
  data: ILocationStrategyData,
  props: ILocationStrategyProps,
  contentStyles: Ref<Record<string, string>>
) => undefined | { updateLocation: (event: Event | HTMLElement[]) => void };

const locationStrategies = {
  static: staticLocationStrategy,
  connected: connectedLocationStrategy
};

export interface ILocationStrategyProps {
  locationStrategy: keyof typeof locationStrategies | TLocationStrategyFn;
  location: TAnchor;
  origin: TAnchor | 'auto' | 'overlap';
  offset?: number | string | number[];
  maxHeight: number | string;
  maxWidth: number | string;
  minHeight: number | string;
  minWidth: number | string;
}

export interface ILocationProps {
  location: TAnchor;
}

export interface IUseLocationStrategies {
  contentStyles: Ref<Record<string, string>>;
  updateLocation: Ref<((event: Event | HTMLElement[]) => void) | undefined>;
}

export const makeLocationStrategyProps = propsFactory(
  {
    /**
     * Стратегия позиционирования компонента
     */
    locationStrategy: {
      type: [String, Function] as PropType<keyof typeof locationStrategies | TLocationStrategyFn>,
      default: 'static',
      validator: (value: any) => typeof value === 'function' || value in locationStrategies
    },
    /**
     * Выравнивание компонент по краям top, bottom, left, right. Можно комбинировать, например, top left
     */
    location: {
      type: String as PropType<TAnchor>,
      default: 'bottom'
    },
    /**
     * Откуда будет рассчитываться появление компонента
     */
    origin: {
      type: String as PropType<TAnchor | 'auto' | 'overlap'>,
      default: 'auto'
    },
    /**
     * Отступы компонента от активатора
     */
    offset: {
      type: [Number, String, Array] as PropType<number | string | number[]>,
      default: null
    }
  },
  'location-strategies'
);

export const makeLocationProps = propsFactory(
  {
    /**
     * Выравнивание компонент по краям top, bottom, left, right. Можно комбинировать, например, top left
     */
    location: {
      type: String as PropType<TAnchor>,
      default: 'bottom'
    }
  },
  'location'
);

export function useLocationStrategies(
  props: ILocationStrategyProps,
  data: ILocationStrategyData
): IUseLocationStrategies {
  const contentStyles = ref<Record<string, string>>({});
  const updateLocation = ref<(args: Event | HTMLElement[], elementSelector?: string) => void>();

  if (IN_BROWSER) {
    useToggleScope(
      () => !!(data.isActive.value && props.locationStrategy),
      (reset) => {
        watch(() => props.locationStrategy, reset);
        onScopeDispose(() => {
          window.removeEventListener('resize', onResize);
          updateLocation.value = undefined;
        });

        window.addEventListener('resize', onResize, { passive: true });

        if (typeof props.locationStrategy === 'function') {
          updateLocation.value = props.locationStrategy(data, props, contentStyles)?.updateLocation;
        } else {
          updateLocation.value = locationStrategies[props.locationStrategy](data, props, contentStyles)?.updateLocation;
        }
      }
    );
  }

  function onResize(event: Event) {
    updateLocation.value?.(event);
  }

  return {
    contentStyles,
    updateLocation
  };
}

const oppositeMap = {
  center: 'center',
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left'
} as const;
export function useLocation(
  props: ILocationProps,
  opposite = false,
  offset?: (side: string) => number,
  observedElement?: string
) {
  if (observedElement) {
    const { elements } = useDomObserver(observedElement);

    const { updateLocation } = staticLocationStrategy();

    watch(elements, (newElements) => {
      updateLocation(newElements);
    });
  }

  const locationStyles = computed(() => {
    if (!props.location) return {};

    const { side, align } = parseAnchor(
      props.location.split(' ').length > 1 ? props.location : (`${props.location} center` as TAnchor)
    );

    function getOffset(side: string) {
      return offset ? offset(side) : 0;
    }

    const styles = {} as CSSProperties;

    if (side !== 'center') {
      if (opposite) {
        styles[oppositeMap[side]] = `calc(100% - ${getOffset(side)}px`;
      } else {
        styles[side] = 0;
      }
    }

    if (align !== 'center') {
      if (opposite) {
        styles[oppositeMap[align]] = `calc(100% - ${getOffset(align)}px`;
      } else {
        styles[align] = 0;
      }
    } else {
      if (side === 'center') {
        styles.top = styles.left = '50%';
      } else {
        styles[
          (
            {
              top: 'left',
              bottom: 'left',
              left: 'top',
              right: 'top'
            } as const
          )[side]
        ] = '50%';
      }

      styles.transform = {
        top: 'translateX(-50%)',
        bottom: 'translateX(-50%)',
        left: 'translateY(-50%)',
        right: 'translateY(-50%)',
        center: 'translate(-50%, -50%)'
      }[side];
    }

    return styles;
  });

  return { locationStyles };
}

type Position = 'top' | 'bottom';
type Alignment = 'left' | 'center' | 'right';

function staticLocationStrategy() {
  function updateLocation(elements: HTMLElement[] | Event): void {
    if (elements instanceof Event) return;

    requestAnimationFrame(() => {
      // Группируем элементы по их положению
      const groups: Record<Position, Record<Alignment, HTMLElement[]>> = {
        top: { left: [], center: [], right: [] },
        bottom: { left: [], center: [], right: [] }
      };

      elements.forEach((element) => {
        const verticalPosition: Position = element.style.top === '0px' ? 'top' : 'bottom';
        const horizontalPosition: Alignment =
          element.style.left === '0px' ? 'left' : element.style.right === '0px' ? 'right' : 'center';
        groups[verticalPosition][horizontalPosition].push(element);
      });

      Object.entries(groups).forEach(([verticalPosition, horizontalGroups]) => {
        Object.entries(horizontalGroups).forEach(([horizontalPosition, elements]) => {
          elements.forEach((notificationElement, index) => {
            const height = (notificationElement?.offsetHeight || 0) + NOTIFICATION_MARGIN;
            const offsetElement = (elements.length - 1 - index) * height;

            const transformValue =
              verticalPosition === 'top' ? `translateY(${offsetElement}px)` : `translateY(-${offsetElement}px)`;

            // Для горизонтального позиционирования
            if (horizontalPosition === 'center') {
              notificationElement.style.left = '50%';
              notificationElement.style.transform = `translateX(-50%) ${transformValue}`;
            } else {
              notificationElement.style.transform = transformValue;
            }
          });
        });
      });
    });
  }

  return { updateLocation };
}

function getIntrinsicSize(el: HTMLElement) {
  const contentBox = nullifyTransforms(el);

  el.style.removeProperty('right');

  contentBox.x -= parseFloat(el.style.left || 0);
  contentBox.y -= parseFloat(el.style.top || 0);

  return contentBox;
}

function connectedLocationStrategy(
  data: ILocationStrategyData,
  props: ILocationStrategyProps,
  contentStyles: Ref<Record<string, string>>
) {
  const activatorFixed = Array.isArray(unref(data.target)) || isFixedPosition(unref(data.activatorEl));

  if (activatorFixed) {
    Object.assign(contentStyles.value, {
      position: 'fixed',
      top: 0,
      left: 0
    });
  }

  const { preferredAnchor, preferredOrigin } = destructComputed(() => {
    const parsedAnchor = parseAnchor(props.location);
    const parsedOrigin =
      props.origin === 'overlap'
        ? parsedAnchor
        : props.origin === 'auto'
        ? flipSide(parsedAnchor)
        : parseAnchor(props.origin);

    // Some combinations of props may produce an invalid origin
    if (parsedAnchor.side === parsedOrigin.side && parsedAnchor.align === flipAlign(parsedOrigin).align) {
      return {
        preferredAnchor: flipCorner(parsedAnchor),
        preferredOrigin: flipCorner(parsedOrigin)
      };
    } else {
      return {
        preferredAnchor: parsedAnchor,
        preferredOrigin: parsedOrigin
      };
    }
  });

  const [minWidth, minHeight, maxWidth, maxHeight] = (['minWidth', 'minHeight', 'maxWidth', 'maxHeight'] as const).map(
    (key) => {
      return computed(() => {
        const value = parseFloat(props[key]!);
        return isNaN(value) ? Infinity : value;
      });
    }
  );

  const offset = computed(() => {
    if (Array.isArray(props.offset)) {
      return props.offset;
    }

    if (typeof props.offset === 'string') {
      const offset = props.offset.split(' ').map(parseFloat);
      if (offset.length < 2) {
        offset.push(0);
      }
      return offset;
    }
    return typeof props.offset === 'number' ? [props.offset, 0] : [0, 0];
  });

  let observe = false;
  const observer = new ResizeObserver(() => {
    if (observe) updateLocation();
  });

  watch(
    [data.target, data.contentEl],
    ([newTarget, newContentEl], [oldTarget, oldContentEl]) => {
      if (oldTarget && !Array.isArray(oldTarget)) observer.unobserve(oldTarget);
      if (newTarget && !Array.isArray(newTarget)) observer.observe(newTarget);

      if (oldContentEl) observer.unobserve(oldContentEl);
      if (newContentEl) observer.observe(newContentEl);
    },
    {
      immediate: true
    }
  );

  onScopeDispose(() => {
    observer.disconnect();
  });

  function updateLocation() {
    observe = false;
    requestAnimationFrame(() => (observe = true));

    if (!data.target.value || !data.contentEl.value) return;

    const targetBox = getTargetBox(data.target.value);
    const contentBox = getIntrinsicSize(data.contentEl.value);
    const scrollParents = getScrollParents(data.contentEl.value);

    if (!scrollParents.length) {
      scrollParents.push(document.documentElement);
      if (!(data.contentEl.value.style.top && data.contentEl.value.style.left)) {
        contentBox.x += parseFloat(document.documentElement.style.getPropertyValue('--cds-body-scroll-x') || '0');
        contentBox.y += parseFloat(document.documentElement.style.getPropertyValue('--cds-body-scroll-y') || '0');
      }
    }

    const viewport = scrollParents.reduce<Box>((box: Box | undefined, el) => {
      const rect = el.getBoundingClientRect();
      const scrollBox = new Box({
        x: el === document.documentElement ? 0 : rect.x,
        y: el === document.documentElement ? 0 : rect.y,
        width: el.clientWidth,
        height: el.clientHeight
      });

      if (box) {
        return new Box({
          x: Math.max(box.left, scrollBox.left),
          y: Math.max(box.top, scrollBox.top),
          width: Math.min(box.right, scrollBox.right) - Math.max(box.left, scrollBox.left),
          height: Math.min(box.bottom, scrollBox.bottom) - Math.max(box.top, scrollBox.top)
        });
      }
      return scrollBox;
    }, undefined!);

    viewport.x += VIEWPORT_MARGIN;
    viewport.y += VIEWPORT_MARGIN;
    viewport.width -= VIEWPORT_MARGIN * 2;
    viewport.height -= VIEWPORT_MARGIN * 2;

    let placement = {
      anchor: unref(preferredAnchor),
      origin: unref(preferredOrigin)
    };

    function checkOverflow(_placement: typeof placement) {
      const box = new Box(contentBox);
      const targetPoint = anchorToPoint(_placement.anchor, targetBox);
      const contentPoint = anchorToPoint(_placement.origin, box);

      let { x, y } = getOffset(targetPoint, contentPoint);

      switch (_placement.anchor.side) {
        case 'top':
          y -= offset.value[0];
          break;
        case 'bottom':
          y += offset.value[0];
          break;
        case 'left':
          x -= offset.value[0];
          break;
        case 'right':
          x += offset.value[0];
          break;
      }

      switch (_placement.anchor.align) {
        case 'top':
          y -= offset.value[1];
          break;
        case 'bottom':
          y += offset.value[1];
          break;
        case 'left':
          x -= offset.value[1];
          break;
        case 'right':
          x += offset.value[1];
          break;
      }

      box.x += x;
      box.y += y;

      box.width = Math.min(box.width, unref(maxWidth));
      box.height = Math.min(box.height, unref(maxHeight));

      const overflows = getOverflow(box, viewport);

      return { overflows, x, y };
    }

    let x = 0;
    let y = 0;
    const available = { x: 0, y: 0 };
    const flipped = { x: false, y: false };
    let resets = -1;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (resets++ > 10) {
        console.error('Infinite loop detected in connectedLocationStrategy');
        break;
      }

      const { x: _x, y: _y, overflows } = checkOverflow(placement);

      x += _x;
      y += _y;

      contentBox.x += _x;
      contentBox.y += _y;

      // flip
      {
        const axis = getAxis(placement.anchor);
        const hasOverflowX = overflows.x.before || overflows.x.after;
        const hasOverflowY = overflows.y.before || overflows.y.after;

        let reset = false;
        ['x', 'y'].forEach((key) => {
          if ((key === 'x' && hasOverflowX && !flipped.x) || (key === 'y' && hasOverflowY && !flipped.y)) {
            const newPlacement = { anchor: { ...placement.anchor }, origin: { ...placement.origin } };
            const flip = key === 'x' ? (axis === 'y' ? flipAlign : flipSide) : axis === 'y' ? flipSide : flipAlign;
            newPlacement.anchor = flip(newPlacement.anchor);
            newPlacement.origin = flip(newPlacement.origin);
            const { overflows: newOverflows } = checkOverflow(newPlacement);
            if (
              (newOverflows[key].before <= overflows[key].before && newOverflows[key].after <= overflows[key].after) ||
              newOverflows[key].before + newOverflows[key].after < (overflows[key].before + overflows[key].after) / 2
            ) {
              placement = newPlacement;
              reset = flipped[key] = true;
            }
          }
        });
        if (reset) continue;
      }

      // shift
      if (overflows.x.before) {
        x += overflows.x.before;
        contentBox.x += overflows.x.before;
      }
      if (overflows.x.after) {
        x -= overflows.x.after;
        contentBox.x -= overflows.x.after;
      }
      if (overflows.y.before) {
        y += overflows.y.before;
        contentBox.y += overflows.y.before;
      }
      if (overflows.y.after) {
        y -= overflows.y.after;
        contentBox.y -= overflows.y.after;
      }

      // size
      {
        const overflows = getOverflow(contentBox, viewport);
        available.x = viewport.width - overflows.x.before - overflows.x.after;
        available.y = viewport.height - overflows.y.before - overflows.y.after;

        x += overflows.x.before;
        contentBox.x += overflows.x.before;
        y += overflows.y.before;
        contentBox.y += overflows.y.before;
      }

      break;
    }

    const axis = getAxis(placement.anchor);

    Object.assign(unref(contentStyles), {
      '--cds-overlay-anchor-origin': `${placement.anchor.side} ${placement.anchor.align}`,
      '--cds-overlay-flipped-x': `${flipped.x ? 1 : 0}`,
      '--cds-overlay-flipped-y': `${flipped.y ? 1 : 0}`,
      transformOrigin: `${placement.origin.side} ${placement.origin.align}`,
      top: convertToUnit(pixelRound(y)),
      left: convertToUnit(pixelRound(x)),
      right: undefined,
      minWidth: convertToUnit(axis === 'y' ? Math.min(unref(minWidth), targetBox.width) : unref(minWidth)),
      maxWidth: convertToUnit(
        pixelCeil(clamp(available.x, unref(minWidth) === Infinity ? 0 : unref(minWidth), unref(maxWidth)))
      ),
      maxHeight: convertToUnit(
        pixelCeil(clamp(available.y, unref(minHeight) === Infinity ? 0 : unref(minHeight), unref(maxHeight)))
      )
    });

    return {
      available,
      contentBox
    };
  }

  watch(
    () => [
      unref(preferredAnchor),
      unref(preferredOrigin),
      props.offset,
      props.minWidth,
      props.minHeight,
      props.maxWidth,
      props.maxHeight
    ],
    () => updateLocation()
  );

  nextTick(() => {
    const result = updateLocation();

    // TODO: overflowing content should only require a single updateLocation call
    // Icky hack to make sure the content is positioned consistently
    if (!result) return;

    const { available, contentBox } = result;

    if (contentBox.height > available.y) {
      requestAnimationFrame(() => {
        updateLocation();
        requestAnimationFrame(() => {
          updateLocation();
        });
      });
    }
  });

  return { updateLocation };
}

function pixelRound(val: number) {
  return Math.round(val * devicePixelRatio) / devicePixelRatio;
}

function pixelCeil(val: number) {
  return Math.ceil(val * devicePixelRatio) / devicePixelRatio;
}
